import React, { useCallback, useEffect, useState } from 'react';
import clsx from 'clsx';
import { useEditor } from '@craftjs/core';
import { TextField as InspectorTextField } from '@/components/inspector-fields/TextField/TextField';
import { useFormFieldHiddenSync } from '@/hooks/useFormFieldHiddenSync';
import IconClose from '@/assets/icons/icon-close.svg?react';
import IconPlus from '@/assets/icons/icon-plus.svg?react';
import { IconSimpleButton } from '@/components/buttons/IconSimpleButton/IconSimpleButton';
import { SecondaryButton } from '@/components/buttons/SecondaryButton/SecondaryButton';
import { Switch } from '@/components/inspector-fields/Switch/Switch';
import { Textarea } from '@/components/ui/textarea';
import {
  DraggableOptionList,
  type OptionItem,
} from '@/components/inspector-fields/OptionField/DraggableOptionList';
import type { FormDropdownFieldProps } from '@builder4/shared/components/Form';
import { generateUuidV4 } from '@builder4/shared/components/Form/form-utils';
import { useBreakpointAwareProp } from '@/hooks/useBreakpointAwareProp';
import { FORM_DROPDOWN_FIELD_NODE_NAME, FIELD_LABEL_DROPDOWN } from '@/utils/constants';

export interface DropdownFieldProps {
  className?: string;
  /** Title shown in the card header */
  title?: string;
  fieldNodeId: string;
  onClose?: () => void;
  getDefaultFieldLabel: () => string;
}

export const FormDropdownField: React.FC<DropdownFieldProps> = ({
  className = '',
  title = 'Dropdown Field',
  fieldNodeId,
  onClose,
  getDefaultFieldLabel,
}) => {
  const { actions, fieldProps } = useEditor((_, query) => {
    const node = query.node(fieldNodeId).get();
    return {
      fieldProps: (node?.data.props || {}) as FormDropdownFieldProps,
    };
  });

  const { setBPProp, getBPProp } = useBreakpointAwareProp(FORM_DROPDOWN_FIELD_NODE_NAME);

  // Read props using breakpoint-aware getter
  const label = getBPProp(fieldProps, 'label');
  const placeholder = getBPProp(fieldProps, 'placeholder') as string;
  const showLabel = getBPProp(fieldProps, 'showLabel');
  const showHelpText = getBPProp(fieldProps, 'showHelpText');
  const helpText = getBPProp(fieldProps, 'helpText');
  const required = getBPProp(fieldProps, 'required');
  const hidden = getBPProp(fieldProps, 'hidden');
  const nodeOptions = getBPProp(fieldProps, 'options');
  const nodeDefaultIndices = getBPProp(fieldProps, 'defaultIndices');
  const isSingleSelect = getBPProp(fieldProps, 'isSingleSelect');

  // Local state for options management (string array) - initialized from breakpoint-aware props
  const [options, setOptions] = useState<string[]>(() => nodeOptions || []);
  // Use index-based storage to handle duplicate option names correctly
  // Both single-select and multi-select use defaultIndices (single uses first element)
  const [defaultIndices, setDefaultIndices] = useState<number[]>(() => nodeDefaultIndices || []);

  // Adapter functions to work with DraggableOptionList (which uses OptionItem[])
  const optionsToItems = (opts: string[]): OptionItem[] =>
    opts.map((text, index) => ({ id: String(index), text }));

  const itemsToOptions = (items: OptionItem[]): string[] => items.map((item) => item.text);

  // Return the index of the default option for the DraggableOptionList (single-select)
  const getDefaultId = (): string | undefined => {
    return defaultIndices.length > 0 ? String(defaultIndices[0]) : undefined;
  };

  // Get all default IDs for multi-select mode
  const getDefaultIds = (): string[] => {
    return defaultIndices.map((idx) => String(idx));
  };

  // Sync local state to CraftJS node — ignore() prevents undo loops where
  // reverting this entry triggers a reverse sync that re-creates the entry.
  useEffect(() => {
    actions.history.ignore().setProp(fieldNodeId, (props: FormDropdownFieldProps) => {
      setBPProp(props, 'options', options);
      setBPProp(props, 'defaultIndices', defaultIndices);
    });
  }, [options, defaultIndices, fieldNodeId, actions, setBPProp]);

  // Sync breakpoint-aware props to local state when they change
  // Use comparison to avoid infinite loops from circular updates
  useEffect(() => {
    const newOptions = nodeOptions || [];
    setOptions((prev) => {
      if (prev.length === newOptions.length && prev.every((val, idx) => val === newOptions[idx])) {
        return prev; // No change, return same reference
      }
      return newOptions;
    });
  }, [nodeOptions]);

  // Sync CraftJS defaultIndices to local state, with comparison to avoid infinite loops
  useEffect(() => {
    const newIndices = nodeDefaultIndices || [];
    // Only update if values actually changed (compare arrays by content)
    setDefaultIndices((prev) => {
      if (prev.length === newIndices.length && prev.every((val, idx) => val === newIndices[idx])) {
        return prev; // No change, return same reference
      }
      return newIndices;
    });
  }, [nodeDefaultIndices]);

  // Write options/defaultIndices to CraftJS with history tracking
  const commitOptions = useCallback(
    (newOptions: string[], newDefaultIndices: number[]) => {
      setOptions(newOptions);
      setDefaultIndices(newDefaultIndices);
      actions.setProp(fieldNodeId, (props: FormDropdownFieldProps) => {
        setBPProp(props, 'options', newOptions);
        setBPProp(props, 'defaultIndices', newDefaultIndices);
      });
    },
    [actions, fieldNodeId, setBPProp],
  );

  // Handlers (working with string arrays)
  const handleReorder = useCallback(
    (next: OptionItem[]) => {
      // Build a mapping from old index to new index
      const oldToNewIndex = new Map<number, number>();
      next.forEach((item, newIndex) => {
        const oldIndex = parseInt(item.id, 10);
        oldToNewIndex.set(oldIndex, newIndex);
      });

      const newOptions = itemsToOptions(next);
      const newDefaults = defaultIndices
        .map((idx) => oldToNewIndex.get(idx) ?? idx)
        .filter((idx) => idx < newOptions.length);
      commitOptions(newOptions, newDefaults);
    },
    [defaultIndices, commitOptions],
  );

  const handleAddOption = useCallback(() => {
    if (options.length >= 50) return; // Maximum 50 options
    commitOptions([...options, 'New Option'], defaultIndices);
  }, [options, defaultIndices, commitOptions]);

  const handleSetDefault = useCallback(
    (id: string) => {
      const index = parseInt(id, 10);
      if (index >= 0 && index < options.length) {
        let newDefaults: number[];
        if (isSingleSelect) {
          // Single-select: toggle - if already default, unset; otherwise set as new default
          newDefaults = defaultIndices.length > 0 && defaultIndices[0] === index ? [] : [index];
        } else {
          // Multi-select: toggle in array
          newDefaults = defaultIndices.includes(index)
            ? defaultIndices.filter((i) => i !== index)
            : [...defaultIndices, index];
        }
        commitOptions(options, newDefaults);
      }
    },
    [options, defaultIndices, isSingleSelect, commitOptions],
  );

  const handleDuplicate = useCallback(
    (id: string) => {
      const index = parseInt(id, 10);
      if (index < 0 || index >= options.length || options.length >= 50) return;

      const newOptions = [
        ...options.slice(0, index + 1),
        options[index], // Duplicate the value
        ...options.slice(index + 1),
      ];
      // Shift default indices if they come after the duplicated position
      const newDefaults = defaultIndices.map((i) => (i > index ? i + 1 : i));
      commitOptions(newOptions, newDefaults);
    },
    [options, defaultIndices, commitOptions],
  );

  const handleDelete = useCallback(
    (id: string) => {
      if (options.length <= 1) return; // Minimum 1 option for dropdown
      const index = parseInt(id, 10);
      if (index < 0 || index >= options.length) return;

      const newOptions = options.filter((_, i) => i !== index);
      // Update default indices: remove deleted index and shift remaining
      const newDefaults = defaultIndices
        .filter((i) => i !== index)
        .map((i) => (i > index ? i - 1 : i));
      commitOptions(newOptions, newDefaults);
    },
    [options, defaultIndices, commitOptions],
  );

  const handleEdit = useCallback(
    (id: string, text: string) => {
      if (!text.trim()) return; // Option text cannot be blank
      const index = parseInt(id, 10);
      if (index < 0 || index >= options.length) return;

      const newOptions = options.map((opt, i) => (i === index ? text : opt));
      commitOptions(newOptions, defaultIndices);
    },
    [options, defaultIndices, commitOptions],
  );

  // Throttled — batches rapid keystrokes (text inputs)
  const setProp = (callback: (props: FormDropdownFieldProps) => void) => {
    actions.history.throttle(500).setProp(fieldNodeId, callback);
  };

  // Direct — discrete actions (toggles, onBlur)
  const setPropDirect = (callback: (props: FormDropdownFieldProps) => void) => {
    actions.setProp(fieldNodeId, callback);
  };

  // Sync hidden prop with layer visibility
  const { handleHiddenChange } = useFormFieldHiddenSync<FormDropdownFieldProps>(
    fieldNodeId,
    hidden,
    setPropDirect,
  );

  useEffect(() => {
    actions.history.ignore().setCustom(fieldNodeId, (custom) => {
      custom.displayName = label || FIELD_LABEL_DROPDOWN;
    });
  }, [label, actions, fieldNodeId]);

  return (
    <div
      className={clsx(
        'bg-neutral-0 inline-flex h-[480px] max-h-[480px] w-[248px] flex-col items-center overflow-hidden rounded-lg shadow-[0px_12px_24px_0px_rgba(0,0,0,0.12)] outline-[0.8px] outline-neutral-100',
        className,
      )}
      data-craft-history
    >
      {/* Card header */}
      <div className="bg-neutral-0 inline-flex h-10 w-full items-center justify-between rounded-t-lg border border-neutral-100 px-3">
        <div className="text-xs leading-5 font-semibold text-neutral-900">{title}</div>
        <IconSimpleButton aria-label="Close" icon={IconClose} onClick={onClose} />
      </div>

      <div className="flex min-h-0 w-full flex-1 flex-col border border-t-0 border-neutral-100">
        <div className="no-scrollbar flex-1 overflow-y-auto">
          <div className="flex w-full flex-col items-start justify-start gap-3 px-3 pt-3 pb-4">
            {/* Field Label */}
            <InspectorTextField
              className="w-full"
              classNameInputParent="max-w-none"
              classNameInput="max-w-[140px]"
              text="Field Label"
              inputValue={label}
              placeholderText="Dropdown"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setProp((props: FormDropdownFieldProps) => {
                  setBPProp(props, 'label', e.target.value);
                })
              }
              onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                if (!e.target.value) {
                  setProp((props: FormDropdownFieldProps) => {
                    setBPProp(props, 'label', getDefaultFieldLabel());
                  });
                }
              }}
            />

            {/* Placeholder */}
            <InspectorTextField
              className="w-full"
              classNameInputParent="max-w-none"
              classNameInput="max-w-[140px]"
              text="Placeholder"
              inputValue={placeholder}
              placeholderText="Select Options"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setProp((props: FormDropdownFieldProps) => {
                  setBPProp(props, 'placeholder', e.target.value);
                })
              }
            />

            {/* Label toggle */}
            <Switch
              label="Show Label"
              checked={showLabel}
              onCheckedChange={(checked: boolean) =>
                setPropDirect((props: FormDropdownFieldProps) => {
                  setBPProp(props, 'showLabel', checked);
                })
              }
            />

            {/* Single selection toggle (inverted: checked = single select mode) */}
            <Switch
              label="Single selection"
              checked={isSingleSelect}
              onCheckedChange={(checked: boolean) => {
                if (checked && defaultIndices.length > 1) {
                  // Switching to single-select with multiple defaults: keep only first
                  const newDefaultIndices = [defaultIndices[0]];
                  // Update local state first to avoid sync issues
                  setDefaultIndices(newDefaultIndices);
                }
                // Hydra requires a new field ID when the field kind changes (single vs multi select).
                const newId = generateUuidV4();
                // Update CraftJS props
                setPropDirect((props: FormDropdownFieldProps) => {
                  setBPProp(props, 'isSingleSelect', checked);
                  props.id = newId;
                  if (checked && defaultIndices.length > 1) {
                    // Switching to single-select: keep only first default
                    setBPProp(props, 'defaultIndices', [defaultIndices[0]]);
                  }
                  // Switching to multi-select: defaultIndices stays as-is
                });
              }}
            />

            {/* Options Management */}
            <div className="flex w-full flex-col gap-2">
              <div className="text-button-sm leading-4 font-medium text-neutral-500">Options</div>

              <DraggableOptionList
                items={optionsToItems(options)}
                defaultId={isSingleSelect ? getDefaultId() : undefined}
                defaultIds={!isSingleSelect ? getDefaultIds() : undefined}
                isMultiSelect={!isSingleSelect}
                onChange={handleReorder}
                onSetDefault={handleSetDefault}
                onDuplicate={handleDuplicate}
                onDelete={handleDelete}
                onEdit={handleEdit}
                disableDelete={() => options.length <= 1}
              />

              {/* Add Option Button */}
              {options.length < 50 && (
                <div className="flex items-center justify-between">
                  <span className="text-button-sm text-neutral-500">New Option</span>
                  <SecondaryButton size="sm" icon={IconPlus} text="Add" onClick={handleAddOption} />
                </div>
              )}
            </div>

            {/* Divider */}
            <div className="self-stretch pb-1">
              <div className="h-0 border-t border-neutral-100" />
            </div>

            {/* Required, Hidden and Help Text toggles */}
            <div className="flex w-full flex-col">
              <Switch
                label="This field is required"
                checked={required}
                onCheckedChange={(checked: boolean) =>
                  setPropDirect((props: FormDropdownFieldProps) => {
                    setBPProp(props, 'required', checked);
                  })
                }
              />
              <Switch
                label="This is a hidden field"
                checked={hidden}
                onCheckedChange={handleHiddenChange}
              />
              <Switch
                label="Include help text"
                checked={showHelpText}
                onCheckedChange={(checked: boolean) =>
                  setPropDirect((props: FormDropdownFieldProps) => {
                    setBPProp(props, 'showHelpText', checked);
                  })
                }
              >
                <Textarea
                  className="h-[109px] w-full overflow-hidden"
                  placeholder="Add help text"
                  value={helpText}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                    setProp((props: FormDropdownFieldProps) => {
                      setBPProp(props, 'helpText', e.target.value);
                    })
                  }
                />
              </Switch>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FormDropdownField;
